import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      if (authService.isAuthenticated()) {
        const userData = authService.getUser();
        setUser(userData);
        setIsAuthenticated(true);
        
        // Verificar se o token ainda é válido
        try {
          await authService.me();
        } catch (error) {
          // Token inválido, fazer logout
          logout();
        }
      }
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  // Verificar autenticação ao carregar a aplicação
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await authService.login({ email, password });
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao fazer login';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await authService.register(userData);
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao criar conta';
      const errors = error.response?.data?.errors || [];
      return { success: false, message, errors };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      const response = await authService.forgotPassword(email);
      return { success: response.success, message: response.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao enviar email de recuperação';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (token, password, confirmPassword) => {
    try {
      setLoading(true);
      const response = await authService.resetPassword(token, password, confirmPassword);
      return { success: response.success, message: response.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao redefinir senha';
      const errors = error.response?.data?.errors || [];
      return { success: false, message, errors };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await authService.updateProfile(profileData);
      
      if (response.success) {
        setUser(response.data.user);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao atualizar perfil';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setLoading(true);
      const response = await authService.changePassword(passwordData);
      return { success: response.success, message: response.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao alterar senha';
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  // Verificar se o usuário tem uma role específica
  const hasRole = (role) => {
    return user && user.role === role;
  };

  // Verificar se o usuário é admin
  const isAdmin = () => hasRole('admin');

  // Verificar se o usuário é author ou admin
  const canCreateContent = () => hasRole('admin') || hasRole('author');

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    changePassword,
    hasRole,
    isAdmin,
    canCreateContent,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};